import type { Messages } from './messages.fr';

/** Catalogue EN — doit satisfaire `Messages` (parité de clés garantie par le type). */
export const en: Messages = {
  nav: {
    home: 'Home',
    gallery: 'Gallery',
    folders: 'Folders',
    tags: 'Tags',
    aiReview: 'To review (AI)',
    aiReviewCount_one: '{count} photo to review (AI)',
    aiReviewCount_other: '{count} photos to review (AI)',
  },
  breadcrumb: {
    label: 'Breadcrumb',
    home: 'Home',
    exifTool: 'EXIF cleanup',
    exifPublishTool: 'Publish EXIF tags',
    dedupTool: 'Deduplication',
    taxonomy: 'Taxonomy',
    aiTrace: 'AI log',
    admin: 'Admin',
    superadmin: 'Superadmin',
    adminGeneral: 'General',
    adminUsers: 'Accounts',
    sessionLinks: 'Access links',
    apiKeys: 'API keys',
    adminGrants: 'Access',
    adminLogs: 'Logs',
    adminFileErrors: 'File errors',
    adminMissingFiles: 'Missing files',
    adminTrash: 'Trash',
    adminReset: 'Maintenance',
    settings: 'Settings',
    settingsAccount: 'My account',
    settingsAppearance: 'Appearance',
    llmConfig: 'LLM configuration',
  },
  menu: {
    openLanguage: 'Choose language',
    openTheme: 'Choose theme',
  },
  theme: {
    light: 'Light',
    dark: 'Dark',
    system: 'System',
  },
  lang: {
    fr: 'French',
    de: 'German',
    en: 'English',
  },
  pwa: {
    updateAvailable: 'New version available',
    reload: 'Reload',
    reloading: 'Reloading…',
    install: 'Install app',
    iosTitle: 'Install app',
    iosIntro: 'On iPhone or iPad, add {name} to your home screen:',
    iosStep1: 'Tap the Share button in Safari.',
    iosStep2: 'Choose “Add to Home Screen”.',
    iosClose: 'Close',
  },
  gallery: {
    countLabel: 'media',
    loading: 'Loading media…',
    empty: 'No media',
    emptyHint: 'Run a scan to index the library.',
    errorTitle: 'Could not load media',
    retry: 'Retry',
    retryNow: 'Retry now',
    errorRateLimitedTitle: 'Too many requests',
    errorRateLimitedRetryIn: 'Retrying automatically in {seconds} s…',
    errorRateLimitedAuto: 'Retrying automatically.',
    errorRateLimitedExhausted: 'The server is still throttling. Please try again in a moment.',
    errorNetworkTitle: 'Cannot connect to the server',
    errorServerTitle: 'Server error — please retry',
    pageErrorTitle: 'Some photos could not be loaded',
    pageErrorRateLimited: 'Too many requests: some photos could not be loaded.',
    pageErrorNetwork: 'Connection lost: some photos could not be loaded.',
    pageErrorServer: 'Server error: some photos could not be loaded.',
    pageErrorAutoRetry: 'Retrying automatically…',
    noLibrary: 'No library selected',
    noLibraryHint: 'Set VITE_LIBRARY_ID to display a library.',
    thumbnailAlt: 'Thumbnail of {name}',
    tabsLabel: 'Gallery sections',
    activeExifField: 'EXIF filter — field {field}',
    activePublishInSync: 'EXIF filter — in-sync media',
    activePublishOutOfSync: 'EXIF filter — media to publish',
    clearFilter: 'Clear',
    clearExifFilter: 'Clear EXIF filter',
    // "To review (AI)" view: prominent button opening the tagger over the whole filtered set.
    reviewPending_one: 'Review this photo',
    reviewPending_other: 'Review these {count} photos',
    reviewPreparing: 'Preparing…',
    reviewEmpty: 'No photos to review.',
    reviewError: 'Could not prepare the review. Please try again.',
  },
  folders: {
    title: 'Folders',
    empty: 'No folders',
    root: 'Root',
    countLabel_one: '{count} media item',
    countLabel_other: '{count} media items',
    open: 'Open folder {name}',
    filterLabel: 'Filter folders',
    filterPlaceholder: 'Filter folders…',
    sortBy: 'Sort folders by',
    sortName: 'Name',
    sortDate: 'Date',
    sortOrder: 'Order',
    sortAsc: 'Ascending',
    sortDesc: 'Descending',
    subfolders: 'Subfolders',
    emptyBadge: 'Empty',
    subfoldersOnly: 'Subfolders only',
    notIndexed: 'Not in the index',
    photosHere: 'Photos',
    breadcrumbLabel: 'Folder path',
  },
  tagsTab: {
    title: 'Tags',
    empty: 'No tags yet',
    emptyHint: 'Tags will appear after tagging.',
    select: 'Filter on tag {label}',
    countLabel_one: '{count} media item',
    countLabel_other: '{count} media items',
  },
  sort: {
    label: 'Sort & filter',
    open: 'Sort & filter',
    sortBy: 'Sort by',
    random: 'Random',
    takenAt: 'Date taken',
    filename: 'File name',
    sizeBytes: 'Size',
    createdAt: 'Date added',
    viewCount: 'Views',
    rating: 'Rating',
    order: 'Order',
    asc: 'Ascending',
    desc: 'Descending',
    reshuffle: 'Reshuffle',
    reshuffling: 'Shuffling…',
    reshuffleError: 'Reshuffle failed. Try again.',
    type: 'Type',
    typeAll: 'All',
    typeImage: 'Images',
    typeAnimatedImage: 'Animated images',
    typeVideo: 'Videos',
    typeVideoWithSound: 'Videos with sound',
    filterSection: 'Filters',
    untagged: 'Untagged',
    aiReview: 'To review (AI)',
  },
  density: {
    open: 'Choose density',
    S: 'Compact',
    M: 'Standard',
    L: 'Comfortable',
  },
  tools: {
    open: 'Open Tools menu',
    openOverflow: 'More',
    upload: 'Upload files',
    scan: 'Run a scan',
    scanning: 'Scanning…',
    scanFailed: 'Scan failed',
    scanAlreadyRunning: 'A scan is already running or queued. Wait for it to finish.',
    tagAiConfirm:
      'Propose AI tags for the whole library? The proposals will need to be reviewed afterwards.',
    tagAiConfirmSelection:
      'Propose AI tags for the {count} selected media? The proposals will need to be reviewed afterwards.',
    tagAiConfirmTitle: 'Tag with AI',
    tagAiConfirmAction: 'Start tagging',
    tagAiCancel: 'Cancel',
    tagAiQueuing: 'Starting…',
    tagAiNoProvider:
      'No LLM provider configured. A superadmin can set one up in Superadmin → LLM configuration.',
    tagAiDisabled: 'AI features are disabled by the administrator.',
    tagAiFailed: 'Failed to start AI tagging',
    dedup: 'Deduplication',
    exif: 'EXIF cleanup',
    exifPublish: 'Publish tags to EXIF',
    fixExtensions: 'Fix extensions',
    cleanEmptyFolders: 'Clean up empty folders',
    taxonomy: 'Manage tags',
  },
  select: {
    toggle: 'Select {name}',
    // Toggle of the SELECTION MODE (toolbar): enter / leave. Leaving clears the selection.
    enterMode: 'Select',
    exitMode: 'Done',
    selectedCount: '{count} selected',
    // Trigger of the menu grouping the selection actions (Move/Rate/…/Delete).
    actions: 'Actions…',
    clear: 'Clear selection',
    selectAll: 'Select all ({count})',
    deselectAll: 'Deselect all',
    selecting: 'Selecting…',
    selectAllError: 'Could not select all. Please try again.',
    selectAllForbidden: 'You do not have permission to select these media.',
    download: 'Download selection',
    downloading: 'Preparing archive…',
    downloadError: 'Could not download the selection. Please try again.',
    downloadForbidden: 'You do not have permission to download these media.',
    tagSelection: 'Review / validate tags',
    slideshow: 'Slideshow',
  },
  badge: {
    missing: 'Missing from disk',
    video: 'Video',
  },
  viewer: {
    title: 'Viewer',
    open: 'Open {name} in the viewer',
    close: 'Close viewer',
    previous: 'Previous media',
    next: 'Next media',
    download: 'Download original',
    counter: '{index} / {total}',
    showInfo: 'Show file name',
    hideInfo: 'Hide file name',
    showDetail: 'Show detail',
    hideDetail: 'Hide detail',
    loading: 'Loading media…',
    error: 'Could not load media',
    errorNotFound: 'This media no longer exists',
    errorNotFoundHint: 'It may have been deleted or moved.',
    errorForbidden: 'Access to this media denied',
    errorForbiddenHint: 'It is not part of what was shared with you.',
    errorNetwork: 'Cannot reach the server',
    errorNetworkHint: 'Check your connection, then reload the page.',
    errorUnknownHint: 'Reload the page or try again later.',
    retry: 'Retry',
    play: 'Start slideshow',
    pause: 'Pause slideshow',
    loopOn: 'Enable looping',
    loopOff: 'Disable looping',
    speed: 'Slideshow speed',
    speedValue: '{seconds} s',
    cap: 'Maximum length for a long medium',
    capValue: 'max {seconds} s',
    capUnlimited: 'no limit',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    zoomReset: 'Reset zoom',
    zoomLevel: 'Zoom: {percent}%',
    videoUnsupported: 'Your browser cannot play this video.',
    openRating: 'Rate photo',
    closeRating: 'Close rating',
    openComments: 'Comment on photo',
    closeComments: 'Close comments',
    // WRITE actions in the top bar (rotations reuse `rotate.cw/ccw`).
    trash: 'Move this photo to the trash',
    // Undo banner shown right after moving to the trash.
    trashed: '{name} moved to the trash',
    undo: 'Undo',
    // Mobile overflow menu holding the four display toggles; entries name WHAT is
    // displayed (the state is carried by the check mark).
    displayOptions: 'Display options',
    optionRating: 'Rating',
    optionComments: 'Comments',
    optionCaption: 'File name',
    optionDetail: 'Detail',
  },
  publicShare: {
    count_one: '{count} item',
    count_other: '{count} items',
    loading: 'Loading share…',
    empty: 'This share contains no media.',
    openMedia: 'Open media {n}',
    viewerLabel: 'Media {n} of {total}',
    position: '{n} / {total}',
    previous: 'Previous media',
    next: 'Next media',
    download: 'Download the original file',
    close: 'Close',
    goneTitle: 'This link is no longer valid',
    goneBody:
      'The share may have expired or been withdrawn by its author. Ask them for a new link.',
    errorTitle: 'Share unavailable',
    errorBody: 'The share could not be loaded. Check your connection.',
    retry: 'Try again',
  },
  share: {
    create: {
      title: 'Create a public link',
      intro: 'Anyone who receives this link can view it without an account.',
      titleLabel: 'Link title',
      scopeLabel: 'Contents',
      scopeRoot: 'The whole library you can see',
      scopeFolder: 'Folder {folder} and its subfolders',
      scopeSelection: '{count} selected media',
      expiryLabel: 'Expires',
      expiry: { d7: '7 days', d30: '30 days', y1: '1 year', never: 'Never' },
      lightweight: 'Share lightweight copies',
      lightweightHint: 'WebP, 2560 px at most, metadata stripped. Smaller photos keep their size.',
      warnOriginals:
        'The original files will be sent as they are, with their metadata — including where they were taken. Videos are always sent as the original file.',
      warnVideosOnly:
        'Photos will be sent as copies without metadata. Videos are always sent as the original file.',
      cancel: 'Cancel',
      submit: 'Create link',
      submitting: 'Creating…',
      forbidden: 'This content goes beyond what you are allowed to see.',
      failed: 'The link could not be created.',
      doneTitle: 'Link created',
      doneIntro: 'Copy it now and send it on.',
      onceOnly: 'This link is shown only once and cannot be retrieved afterwards.',
      copy: 'Copy',
      done: 'Done',
    },
    list: {
      loading: 'Loading shares…',
      empty: 'No public links yet.',
      failed: 'Shares could not be loaded.',
      retry: 'Try again',
      mediaCount: '{count} media',
      viewCount: '{count} views',
      noExpiry: 'no expiry',
      expiresOn: 'expires on {date}',
      revoke: 'Revoke',
      status: { live: 'Active', expired: 'Expired', revoked: 'Revoked' },
    },
    mineTitle: 'My shares',
    mineIntro: 'The public links you have created.',
    adminTitle: 'Shares',
    adminIntro: 'All public links in the library, whoever created them.',
    short: 'Share',
    photo: 'Share this photo',
    view: 'Share this view',
    copied: 'Link copied',
    linkLabel: 'Link to copy',
    close: 'Close',
  },
  rating: {
    label: 'Rating',
    star: 'Rate {n}/5',
    clear: 'Remove rating',
    error: 'Could not save rating',
    errorReadonly: 'Read-only access: rating cannot be changed',
  },
  comments: {
    bodyLabel: 'Comment',
    bodyPlaceholder: 'Write a comment…',
    add: 'Add',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    edit: 'Edit comment',
    delete: 'Delete comment',
    confirmTitle: 'Delete comment',
    confirmDelete: 'Delete this comment?',
    confirmAction: 'Delete',
    deleting: 'Deleting…',
    edited: 'edited',
    empty: 'No comments',
    error: 'Could not save comment',
    errorReadonly: 'Read-only access: comment cannot be changed',
  },
  info: {
    title: 'Information',
    path: 'Path',
    mime: 'MIME type',
    dimensions: 'Dimensions',
    takenAt: 'Taken',
    duration: 'Duration',
    exif: 'EXIF metadata',
    gps: 'GPS lat/long',
    gpsOpen: 'Open in Google Maps',
    noExif: 'No EXIF data',
    divergence: 'Disk ↔ database divergence',
    missing: 'File missing from disk',
    present: 'Present on disk',
    seenAt: 'Seen on',
  },
  exif: {
    title: 'EXIF cleanup',
    close: 'Close EXIF cleanup',
    intro:
      'Spot which metadata is present, browse the photos that carry a field, then erase a specific field (or everything) in batch across the whole library or a folder. The database remains the source of truth.',
    loading: 'Analyzing library…',
    errorTitle: 'Could not load EXIF analysis',
    retry: 'Retry',
    analysisTitle: 'Presence analysis',
    analysisTotal: '{total} present media analyzed',
    analysisHint:
      'Number of media carrying each field. “View” filters the gallery by that field; “Erase this field” removes it in batch across the chosen scope.',
    groupCapture: 'Capture',
    groupCamera: 'Camera',
    groupRights: 'Rights',
    groupKeywords: 'Keywords',
    fieldPresence: '{present} / {total} present',
    filterByField: 'Filter the gallery by field {field}',
    viewField: 'View',
    eraseField: 'Erase this field',
    eraseFieldAction: 'Erase field {field} on the media in scope',
    cleanedBadge: '✓ cleaned',
    eraseOptionsTitle: 'Erase options',
    eraseScopeAll: 'the whole library',
    eraseScopeFolder: 'the {folder} folder',
    publishTitle: 'Publish tags',
    publishHint:
      'Exports database-validated tags (IPTC:Keywords + XMP dc:subject) to out-of-sync files only.',
    taggedAssets: 'Tagged media',
    inSync: 'In sync',
    outOfSync: 'To publish',
    filterInSync: 'Filter the gallery to in-sync media',
    filterOutOfSync: 'Filter the gallery to media to publish',
    publish: 'Publish',
    previewPublish: 'Preview publish',
    publishToolTitle: 'Publish tags to EXIF',
    publishToolClose: 'Close the publish tool',
    publishToolIntro:
      'Exports database-validated tags to the files’ metadata (IPTC:Keywords + XMP dc:subject). Only out-of-sync files are rewritten.',
    eraseTitle: 'Full erase (advanced)',
    eraseHint:
      'Erases ALL embedded metadata (GPS included). Batch action, irreversible without a backup. To keep a field, use “Erase this field” above.',
    eraseAllOptionsNote:
      'The options above (dry run, _original backup, folder scope) apply to “Erase all” TOO — a dry run changes nothing.',
    erase: 'Erase all',
    folderScope: 'Scope (folder)',
    folderAll: 'Whole library',
    keepOriginal: 'Keep an original (_original)',
    keepOriginalHint: 'Backs up the original file alongside before writing.',
    noBackupWarning:
      'No backup: files will be modified directly, with no safety copy (_original). Irreversible.',
    dryRun: 'Dry run',
    dryRunHint: 'Simulates without modifying files.',
    queuing: 'Queuing…',
    jobQueued: 'Job queued (#{id})',
    jobFailed: 'Could not queue the job',
    errorWriteDisabled:
      'Writing is disabled on the library: cannot erase. Mount /library as writable on the server.',
    eraseConfirmClose: 'Close',
    eraseConfirmWarning: 'This action is irreversible.',
    eraseConfirmCancel: 'Cancel',
    eraseConfirmApply: 'Erase',
    eraseConfirmApplying: 'Queuing…',
    eraseConfirmFieldTitle: 'Erase this EXIF field?',
    eraseConfirmFieldBody:
      'The {field} field will be erased from the metadata of {scope}. Other fields are preserved.',
    eraseConfirmAllTitle: 'Erase all metadata?',
    eraseConfirmAllBody:
      'All metadata of {scope} will be erased (except kept groups). GPS is always erased.',
    autoResyncNote:
      'Erasing automatically updates the affected photos in the index — no need to rescan the whole library. The analysis refreshes when the job finishes.',
    refreshAnalysis: 'Refresh analysis',
    originals: {
      title: '_original backup files',
      hint: 'ExifTool creates a “…_original” file on each write when the backup option is on. Search for them to find out how many you have, then delete them.',
      scan: 'Find backups',
      found: '{count} backups found (total size {size}).',
      none: 'No _original backups found.',
      error: 'Could not search for _original backups.',
      delete: 'Delete backups',
      confirmTitle: 'Delete _original backups?',
      confirmBody:
        '{count} _original backup files in {scope} ({size}) will be permanently deleted. The original files are untouched.',
      confirmApply: 'Delete',
    },
  },
  dedup: {
    title: 'Deduplication',
    close: 'Close the deduplication tool',
    // Tabs (exact copies / similar images).
    tabsLabel: 'Duplicate type',
    tabExact: 'Exact copies',
    tabSimilar: 'Similar images',
    similarTitle: 'Similar images',
    intro:
      'Groups near-duplicates (re-encodes, resizes, light edits) by perceptual similarity. Grouping can be wrong: by default every image is KEPT; only erase true duplicates.',
    loading: 'Loading groups…',
    // Summary (header).
    summaryClusters_one: '{count} group',
    summaryClusters_other: '{count} groups',
    summaryDuplicates_one: '{count} candidate duplicate',
    summaryDuplicates_other: '{count} candidate duplicates',
    lastRunAt: 'Last analysis: {when}',
    neverRun: 'Never analyzed',
    // Analysis.
    thresholdLabel: 'Similarity threshold',
    thresholdHint: 'Maximum Hamming distance (0 = identical, higher = more permissive).',
    runAnalysis: 'Run an analysis',
    running: 'Queuing…',
    analysisQueued: 'Analysis queued. Follow progress in the bell, then refresh.',
    refresh: 'Refresh groups',
    // Group members.
    resolution: '{width} × {height}',
    resolutionUnknown: 'Unknown resolution',
    keeperSuggested: 'Suggested original',
    openInViewer: 'Open {name} in the viewer',
    // Per-image decision (Keep / Erase).
    decisionLabel: 'Decision for {name}',
    decisionKeep: 'Keep',
    decisionDelete: 'Erase',
    lastKeptHint: 'Cannot erase the last kept image in the group.',
    // Apply.
    applyButton: 'Delete selected duplicates',
    applyNone: 'No duplicates selected',
    selectedForDeletion_one: '{count} file to delete',
    selectedForDeletion_other: '{count} files to delete',
    // Confirmation modal.
    confirmTitle: 'Permanently delete duplicates?',
    confirmClose: 'Close',
    confirmWarning: 'This action is irreversible.',
    confirmBody:
      '{count} files will be DELETED from disk. The chosen originals are kept. Thumbnails and the index are updated on the next scan.',
    confirmCancel: 'Cancel',
    confirmApply_one: 'Delete {count} file',
    confirmApply_other: 'Delete {count} files',
    applying: 'Queuing…',
    applyRunning: 'Deletion in progress…',
    applyQueued: 'Deletion queued. Follow progress in the bell.',
    // Dismiss a false-positive group (these are not duplicates).
    notDuplicates: 'These are not duplicates',
    dismissing: 'Dismissing…',
    dismissError: 'Could not dismiss the group.',
    dismissForbidden: 'Write access required to dismiss a group.',
    // Errors.
    errorTitle: 'Could not load duplicate groups.',
    retry: 'Retry',
    errorWriteDisabled:
      'Writing is disabled on the library: deletion is not possible. Mount /library as writable on the server.',
    errorApply: 'Could not queue the deletion.',
    errorRun: 'Could not queue the analysis.',
    // Empty.
    emptyNeverRun: 'No analysis has run yet. Run an analysis to detect duplicates.',
    emptyNoDuplicates: 'No duplicates found. Your library is clean.',
    // Pagination.
    prevPage: 'Previous page',
    nextPage: 'Next page',
    pageStatus: 'Groups {from}–{to} of {total}',
    // --- EXACT copies (distinct section, at the top of the screen) ---
    exact: {
      title: 'Exact copies',
      intro:
        'Strictly identical files (same pixels) present in several places on disk. The inventory comes from the scan. Pick the path to keep per image; the other copies will be deleted.',
      refresh: 'Refresh',
      loading: 'Loading exact copies…',
      // Summary.
      summaryGroups_one: '{count} exact-copy group',
      summaryGroups_other: '{count} exact-copy groups',
      summaryRedundant_one: '{count} redundant copy',
      summaryRedundant_other: '{count} redundant copies',
      inventoryAt: 'Inventory up to date as of {when}',
      neverScanned: 'Never scanned',
      // Group.
      resolution: '{width} × {height}',
      resolutionUnknown: 'Unknown resolution',
      kindImage: 'Image',
      kindVideo: 'Video',
      noThumbnail: 'Preview unavailable',
      openInViewer: 'Open {name} in the viewer',
      canonical: 'Indexed',
      identicalBytes: 'identical bytes',
      reencoded: 're-encoded (identical pixels)',
      // Choice of the path to keep.
      chooseKeepLabel: 'Choose the path to keep',
      willKeep: 'Will be kept',
      willDelete: 'Will be deleted',
      // Reason for the default keeper (icon + text, never color alone).
      keptLightest: 'smallest',
      keptCanonical: 'indexed copy',
      // Apply.
      applyButton: 'Delete redundant copies',
      applyNone: 'No copy to delete',
      selectedForDeletion_one: '{count} copy to delete',
      selectedForDeletion_other: '{count} copies to delete',
      pageScopeNote:
        'Deletion applies to the groups on the displayed page. Process each page before switching pages.',
      // Confirmation modal.
      confirmTitle: 'Permanently delete redundant copies?',
      confirmClose: 'Close',
      confirmWarning: 'This action is irreversible.',
      confirmBody:
        '{count} redundant copies will be DELETED from disk. One copy is kept per image. The index is updated on the next scan.',
      confirmCancel: 'Cancel',
      confirmApply_one: 'Delete {count} copy',
      confirmApply_other: 'Delete {count} copies',
      applying: 'Queuing…',
      applyRunning: 'Deletion in progress…',
      applyQueued: 'Deletion queued. Follow progress in the bell.',
      // Errors.
      errorTitle: 'Could not load exact copies.',
      retry: 'Retry',
      errorWriteDisabled:
        'Writing is disabled on the library: deletion is not possible. Mount /library as writable on the server.',
      errorApply: 'Could not queue the deletion.',
      // Empty.
      emptyNeverScanned: 'No scan has been run — run a scan to detect exact copies.',
      emptyNoCopies: 'No exact copies found.',
      // Pagination.
      prevPage: 'Previous page',
      nextPage: 'Next page',
      pageStatus: 'Groups {from}–{to} of {total}',
    },
  },
  passwordField: {
    show: 'Show password',
    hide: 'Hide password',
  },
  auth: {
    title: 'Sign in',
    emailPlaceholder: 'you@example.com',
    identifierLabel: 'Username or email',
    passwordLabel: 'Password',
    stayLoggedIn: 'Stay signed in',
    submit: 'Sign in',
    submitting: 'Signing in…',
    invalidCredentials: 'Incorrect credentials.',
    genericError: 'Sign-in failed. Please try again.',
    checkingSession: 'Checking session…',
    networkErrorTitle: 'Unable to reach the server.',
    retry: 'Retry',
    tooManyAccounts: 'Too many accounts signed in at once.',
    cancelAddAccount: 'Cancel',
    account: 'Account',
    signOut: 'Sign out',
    roleSuperadmin: 'Superadmin',
    roleAdmin: 'Administrator',
    roleUser: 'User',
  },
  accountSwitcher: {
    title: 'Signed-in accounts',
    addAccount: 'Add an account',
    active: 'Active account',
    logoutAccount: 'Sign out this account',
    confirmLogoutAll: 'Confirm: sign out all?',
    theme: 'Theme',
    language: 'Language',
    myAccount: 'My account',
  },
  settings: {
    title: 'Settings',
    close: 'Close',
    intro: 'Review your account and change your password.',
    tabAccount: 'Account',
    tabAppearance: 'Appearance',
    defaultSortLabel: 'Default sort order for the home page',
    defaultSortHint:
      'Order applied on the home page; elsewhere (Folders, Tags), the last sort you picked is kept.',
    accountTitle: 'Account',
    emailLabel: 'Email address',
    nameLabel: 'Display name',
    roleLabel: 'Role',
    changePasswordTitle: 'Change password',
    currentPasswordLabel: 'Current password',
    passwordViaLink:
      'You signed in through a shared access link: this account has no personal password to change. Ask an administrator for a new link if needed.',
    newPasswordLabel: 'New password',
    confirmPasswordLabel: 'Confirm new password',
    submit: 'Change password',
    submitting: 'Changing…',
    success: 'Password changed. Your other sessions have been signed out.',
    errorCurrentWrong: 'Current password is incorrect.',
    errorMismatch: 'The two new passwords do not match.',
    errorTooShort: 'The new password must be at least 8 characters long.',
    genericError: 'Something went wrong. Please try again.',
    apiKeys: {
      title: 'API keys',
      intro: 'Create keys to access your library read-only from an external tool (API v1).',
      labelLabel: 'Key label',
      labelPlaceholder: 'E.g. My export script',
      create: 'Create a key',
      creating: 'Creating…',
      secretWarning: 'Copy this key now: it will not be shown again.',
      copy: 'Copy',
      copied: 'Copied',
      loading: 'Loading…',
      empty: 'No API keys.',
      neverUsed: 'Never used',
      lastUsed: 'Last used: {date}',
      revoked: 'Revoked',
      revoke: 'Revoke',
      confirmRevoke: 'Confirm revocation',
      delete: 'Delete',
      deleteConfirm: 'Confirm deletion',
      cancel: 'Cancel',
      errorLabel: 'A label is required.',
      errorGeneric: 'Could not create the key. Please try again.',
      errorLoad: 'Could not load keys.',
      docLink: 'View the API documentation (OpenAPI)',
    },
  },
  superadmin: {
    open: 'Superadmin',
    title: 'Superadmin',
  },
  sessionLinks: {
    tab: 'Access links',
    title: 'Access links',
    intro:
      'A link opens a password-free session for an existing account (reader or contributor). Never share the URL over a public channel.',
    createTitle: 'Create an access link',
    targetLabel: 'Target account',
    chooseTarget: 'Choose an account…',
    labelLabel: 'Label',
    labelPlaceholder: 'e.g. “Family” or “Photo club”',
    create: 'Create link',
    creating: 'Creating…',
    writeWarning:
      'Warning: this link grants WRITE access (upload, deletion) to anyone who holds it.',
    urlWarning: 'Copy this URL now: it will never be shown again.',
    copy: 'Copy URL',
    copied: 'Copied',
    errorNoTarget: 'Choose a target account.',
    errorLabel: 'Enter a label.',
    errorTarget: 'This account cannot receive an access link (superadmin account or no library).',
    errorGeneric: 'Creation failed. Please try again.',
    errorLoad: 'Could not load links.',
    retry: 'Try again',
    loading: 'Loading…',
    empty: 'No access links.',
    created: 'Created {date}',
    lastUsed: 'last used {date}',
    neverUsed: 'never used',
    active: 'Active',
    revoked: 'Revoked',
    revoke: 'Revoke',
    revoking: 'Revoking…',
    confirmRevokeTitle: 'Revoke this link?',
    confirmRevokeBody:
      'The link “{label}” will stop working immediately, and anyone who came in through it will be signed out. Other links to the same account are unaffected. This action cannot be undone.',
    cancel: 'Cancel',
  },
  admin: {
    open: 'Admin',
    title: 'Administration',
    close: 'Close',
    tabGeneral: 'General',
    tabUsers: 'Accounts',
    tabGrants: 'Access',
    loading: 'Loading…',
    errorTitle: 'Unable to load.',
    retry: 'Retry',
    // General section (site name, LOT 3).
    general: {
      title: 'General',
      intro: 'Site name shown in the browser tab, the footer and the app install prompt.',
      appNameLabel: 'Site name',
      save: 'Save',
      saveAppNameAria: 'Save site name',
      saveGatewayAria: 'Save authentication gateway',
      saving: 'Saving…',
      saved: 'Site name saved.',
      error: 'Could not save. Please try again.',
      conflict:
        'These settings were changed elsewhere in the meantime. Reload the page before saving, so you do not overwrite the other editor’s change.',
      // Enlarging small photos (viewer).
      upscaleTitle: 'Enlarging small photos',
      upscaleIntro:
        'On large screens, a photo smaller than the window is enlarged in the viewer up to this percentage (100% = original size, no enlargement).',
      upscaleLabel: 'Maximum enlargement (%)',
      upscaleHelp: 'Between {min} and {max}%. Default: 150%.',
      saveUpscaleAria: 'Save small-photo enlargement',
      upscaleSaved: 'Enlargement saved.',
      upscaleInvalid: 'Invalid value. Expected a whole number between {min} and {max}.',
      // Authentication gateway (ADR-0008).
      gatewayTitle: 'Authentication gateway',
      gatewayIntro:
        'Lets members of another application (a forum, for instance) open photo links they share with each other, without having to sign in here. Leave the field empty to disable it: visitors without a session will see the usual sign-in screen.',
      gatewayLabel: 'Gateway URL, in the other application',
      gatewayPlaceholder: 'https://forum.example.ch/photos',
      gatewayHowTitle: 'What that address must do, in the other application:',
      gatewayStep1:
        'Be reachable only by members signed in to that other application: it knows who the visitor is — we do not.',
      gatewayStep2:
        'Read the “next” parameter we pass to it: it holds the page requested here (for example /dossiers?dossier=Trombis).',
      gatewayStep3:
        'Send the visitor back to that page, appending an access link token (?access=…) kept in its own configuration. The token therefore never travels in the links members share.',
      gatewayWarning:
        'Security: the other application must reject any “next” that is not a path starting with a single slash — otherwise the token can be diverted to a third-party site.',
      gatewaySaved: 'Gateway saved.',
      gatewayDisabled: 'Gateway disabled.',
      gatewayInvalid: 'Invalid address. Expected a URL starting with http:// or https://.',
    },
    usersTitle: 'User accounts',
    usersHint: 'Create accounts, change roles or delete them.',
    usersEmpty: 'No accounts.',
    colEmail: 'Email',
    colName: 'Name',
    colRole: 'Role',
    colActions: 'Actions',
    createUserTitle: 'Create an account',
    fieldEmail: 'Email address',
    fieldName: 'Display name',
    fieldRole: 'Role',
    fieldPassword: 'Password',
    grantFullRead: 'Read access to the entire library',
    grantFullReadHint:
      'Uncheck for a reader restricted to a subfolder (set it afterwards under “Access”).',
    adminWriteHint: 'An administrator has write access to the entire library.',
    create: 'Create',
    creating: 'Creating…',
    userCreated: 'Account created.',
    editRole: 'Change role for {name}',
    delete: 'Delete',
    deleting: 'Deleting…',
    confirmDeleteTitle: 'Delete account',
    confirmDelete: 'Delete account {name}? This action cannot be undone.',
    cancel: 'Cancel',
    // Password reset by the superadmin (revealable inline field).
    resetPassword: 'Reset password',
    resetPasswordFor: 'New password for {name}',
    resetPasswordPlaceholder: 'New password (min. 8)',
    resetPasswordSubmit: 'Confirm',
    resettingPassword: 'Resetting…',
    resetPasswordDone: 'Password reset. This account’s sessions have been signed out.',
    resetPasswordError: 'Reset failed. Please try again.',
    conflictUser: 'This account conflicts with an existing account.',
    conflictEmail: 'This email is already in use.',
    conflictLastAdmin: 'Not possible: this is the last superadmin.',
    cannotDeleteSelf: 'You cannot delete your own account.',
    deleteSelfDisabled: 'Your account',
    genericError: 'Something went wrong. Please try again.',
    grantsTitle: 'Library access',
    grantsHint: 'Grant READ-ONLY access to a subtree of the library.',
    grantsReadOnly: 'These grants are read-only: the beneficiary cannot edit or delete.',
    grantsEmpty: 'No access granted.',
    noLibrarySelected: 'No library is associated with your account.',
    colBeneficiary: 'Beneficiary',
    colSubtree: 'Subtree',
    subtreeRoot: 'Root (entire library)',
    createGrantTitle: 'Grant access',
    fieldBeneficiary: 'Beneficiary',
    fieldUserId: 'User ID',
    chooseBeneficiary: 'Choose a beneficiary…',
    fieldSubtree: 'Subtree (path prefix)',
    subtreePlaceholder: 'e.g. 2024/holidays (empty = root)',
    grant: 'Grant',
    granting: 'Granting…',
    grantCreated: 'Access granted.',
    revoke: 'Revoke',
    revoking: 'Revoking…',
    confirmRevokeTitle: 'Revoke access',
    confirmRevoke: 'Revoke access for {name}?',
    conflictGrant: 'This access already exists.',
    notFoundUser: 'User not found.',
    // Unified purge control (reusable — file errors AND request logs).
    purge: {
      dateLabel: 'Up to this date (inclusive)',
      button: 'Purge up to this date',
      title: 'Purge entries',
      confirm: 'Purge all entries up to and including {date}? This action cannot be undone.',
      confirmButton: 'Purge',
      cancel: 'Cancel',
      purging: 'Purging…',
      purged: '{count} entries deleted.',
      invalidDate: 'Invalid date.',
      error: 'Purge failed. Please try again.',
    },
    // Logs section (superadmin) — logging of every API request.
    audit: {
      tab: 'Audit',
      title: 'Audit log',
      intro:
        'Who did what: sign-ins, password changes and processing jobs (move, EXIF erase, purge, compression…), with their author.',
      retention: 'Automatic retention: 24 months. This log cannot be cleared from this page.',
      empty: 'No entries in the log.',
      colTimestamp: 'Timestamp',
      colAction: 'Action',
      colUser: 'Author',
      colSummary: 'Summary',
      colDetails: 'Details',
      noUser: 'Unattributed',
      dryRun: 'Dry run (nothing changed)',
      showDetails: 'Show details',
      detailsUnreadable: 'Details unreadable',
      previous: 'Previous',
      next: 'Next',
      range: '{from}–{to} of {total}',
      filterAction: 'Action',
      filterUser: 'Author',
      filterFrom: 'From',
      filterTo: 'To',
      actionAll: 'All actions',
      userAll: 'All authors',
    },
    sessions: {
      button: 'Sessions',
      count: '{count} active session(s)',
      empty: 'No active session.',
      loadError: 'Could not load sessions.',
      unknownAgent: 'Unknown device',
      unknownLink: 'deleted link',
      lastUsed: 'Last activity: {when}',
      expires: 'Expires on {when}',
      viaLink: 'Entered via the link “{label}”',
      current: 'Your current session',
      revoke: 'Revoke',
      revoking: 'Revoking…',
      revokeError: 'Revocation failed. Try again.',
    },
    logs: {
      tab: 'Logs',
      title: 'Request logs',
      intro: 'Every API request is logged (method, path, status, duration, user).',
      retention: 'Automatic retention: 3 months.',
      empty: 'No entries in the log.',
      colTimestamp: 'Timestamp',
      colMethod: 'Method',
      colPath: 'Path',
      colStatus: 'Status',
      colUser: 'User',
      colDuration: 'Duration',
      colError: 'Error',
      noUser: '—',
      duration: '{ms} ms',
      statusSuccess: 'Success',
      statusRedirect: 'Redirect',
      statusClientError: 'Client error',
      statusServerError: 'Server error',
      previous: 'Previous',
      next: 'Next',
      range: '{from}–{to} of {total}',
      // Filter bar (User / Status = HTTP class / Method = HTTP verb).
      filterUser: 'User',
      filterStatus: 'Status',
      filterMethod: 'Method',
      userAll: 'All users',
      statusAll: 'All statuses',
      status2xx: '2xx (success)',
      status3xx: '3xx (redirect)',
      status4xx: '4xx (client error)',
      status5xx: '5xx (server error)',
      methodAll: 'All methods',
      methodGet: 'GET',
      methodPost: 'POST',
      methodPatch: 'PATCH',
      methodDelete: 'DELETE',
    },
    // File errors section (Lot C) — log of failed file processing operations.
    fileErrors: {
      tab: 'File errors',
      title: 'File processing errors',
      intro:
        'Failures and anomalies encountered while processing (scan, move, EXIF, deduplication): unreadable file, invalid format (e.g. a PDF named .png), failed thumbnail…',
      empty: 'No errors logged.',
      noValue: '—',
      previous: 'Previous',
      next: 'Next',
      range: '{from}–{to} of {total}',
      // Columns.
      colTimestamp: 'Timestamp',
      colSeverity: 'Status',
      colMethod: 'Method',
      colUser: 'User',
      colPath: 'Relative path',
      colMime: 'Detected MIME',
      colMessage: 'Message',
      // Filter bar.
      filterUser: 'User',
      filterStatus: 'Status',
      filterMethod: 'Method',
      userAll: 'All users',
      statusAll: 'All statuses',
      statusError: 'Errors',
      statusSkipped: 'Skipped',
      statusWarning: 'Warnings',
      methodAll: 'All methods',
      methodScan: 'Scan',
      methodMove: 'Move',
      methodExif: 'EXIF',
      methodDedup: 'Deduplication',
      methodTrash: 'Trash purge',
      // Job type labels in the table.
      jobScan: 'Scan',
      jobThumbnail: 'Thumbnail',
      jobAiTag: 'AI tag',
      jobDedup: 'Deduplication',
      jobDedupApply: 'Delete duplicates',
      jobDedupExactApply: 'Delete exact copies',
      jobExifPublish: 'EXIF publish',
      jobExifErase: 'EXIF erase',
      jobMove: 'Move',
      jobCompress: 'Compression',
      jobRotate: 'Rotation',
      jobOriginalsCleanup: '_original cleanup',
      jobTrashPurge: 'Trash purge',
      jobRenameExtensions: 'Extension fix',
      jobEmptyFoldersCleanup: 'Empty folders cleanup',
      // Disk ↔ index reconciliation card (last scan).
      reconTitle: 'Last scan reconciliation',
      reconSummary:
        '{walked} files on disk = {indexed} indexed (in gallery) + {collapsed} exact copies merged + {skipped} skipped.',
      reconWhen: 'Last scan finished on {when}.',
      reconWhenDry: 'Last preview (dry-run) on {when}.',
      reconWalked: 'Files on disk',
      reconIndexed: 'Indexed media (in gallery)',
      reconCollapsed: 'Exact copies merged',
      reconSkipped: 'Skipped files',
      reconByReason: 'Skipped, by reason',
      reconUnaccounted: 'Unaccounted difference: {count} (run a scan to refresh).',
      reconReasonUnsupported: 'Unsupported extension',
      reconReasonNonMedia: 'Non-media file (sidecar / system)',
      reconReasonUnreadable: 'Unreadable file',
      reconReasonDecode: 'Decode / processing failed',
      reconReasonMismatched: 'Type mismatch (e.g. PDF named .png)',
      // Unsupported-extension breakdown + TXT exports.
      reconExtensionsTitle: 'Unsupported extensions, by occurrence',
      reconExtensionNone: '(no extension)',
      reconDownloadSkipped: 'Download the list of skipped files (.txt)',
      reconDownloadNonMedia: 'Download non-media files (.txt)',
    },
    // Maintenance section (superadmin) — reset to "start from scratch".
    reset: {
      tab: 'Maintenance',
      title: 'Reset the library',
      intro:
        'Start from scratch to retest entering the gallery and thumbnail generation. A new scan will rebuild everything.',
      deletes: 'Will be deleted: the media index (database) and the thumbnails (cache).',
      preserves:
        'Will be preserved: your ORIGINAL files and their EXIF metadata (never touched), the accounts, the library and the access grants.',
      button: 'Reset everything',
      // Heavy confirmation modal.
      modalTitle: 'Reset the library?',
      modalWarning:
        'This action is IRREVERSIBLE. It clears the media index and deletes all thumbnails.',
      modalSafe:
        'No original file is modified or deleted, and no EXIF metadata on disk is touched: the reset only affects the database and the thumbnail cache.',
      confirmLabel: 'To confirm, type {word} below.',
      confirmWord: 'RESET',
      confirmPlaceholder: 'Type the confirmation word',
      confirm: 'Reset permanently',
      resetting: 'Resetting…',
      cancel: 'Cancel',
      close: 'Close',
      done: 'Reset complete: {assets} media and {thumbnails} thumbnail(s) deleted. You can start a scan.',
      error: 'The reset failed. Please try again.',
    },
    // Trash section (Lot 2) — trash management (soft-delete + permanent purge).
    // "Missing files" section (audit A5) — media whose file vanished from disk.
    missingFiles: {
      tab: 'Missing files',
      title: 'Missing files',
      intro:
        'Indexed media whose file has VANISHED from disk. They are hidden from the whole gallery: this is the only place left to deal with them.',
      hint: 'Move them to the trash, then empty it (“Trash” tab) to permanently delete the row and its thumbnails. No file is touched: there is none left.',
      empty: 'No missing files. The index matches the disk.',
      error: 'The operation failed. Try again.',
      errorForbidden: 'Operation refused: read-only access.',
      trashSelected: 'Move to trash ({count})',
      trashed: '{count} item(s) moved to the trash.',
      refresh: 'Refresh',
      selectAll: 'Select every row on this page',
      selectOne: 'Select {name}',
      colPath: 'Path',
      colSize: 'Size',
      colSeenAt: 'Last seen',
      seenUnknown: 'Unknown',
      range: '{from}–{to} of {total}',
      previous: 'Previous',
      next: 'Next',
    },
    trash: {
      tab: 'Trash',
      title: 'Trash',
      intro:
        'Media moved to the trash: restore them or purge them PERMANENTLY (deletes the file from disk, irreversible).',
      empty: 'The trash is empty.',
      error: 'The operation failed. Please try again.',
      errorWriteDisabled:
        'Purge not possible: the library is mounted read-only (writing disabled).',
      restoreSelected: 'Restore selection ({count})',
      restored: '{count} media restored.',
      purgeSelected: 'Delete permanently ({count})',
      purgeAll: 'Empty the trash',
      purgeQueued: 'Purge started — follow its progress in the notifications.',
      purging: 'Purging…',
      cancel: 'Cancel',
      confirmTitle: 'Permanent purge?',
      confirmButton: 'Purge permanently',
      confirmWarning:
        'This action is IRREVERSIBLE: the files are DELETED from disk and removed from the database.',
      confirmAll: '{count} media in the trash will be permanently deleted.',
      confirmSelection: '{count} selected media will be permanently deleted.',
      deletedWhen: 'Deleted on {when}',
      range: '{from}–{to} of {total}',
      previous: 'Previous',
      next: 'Next',
    },
  },
  bugReport: {
    open: 'Report a bug',
    title: 'Report a bug',
    close: 'Close',
    intro:
      'Copy this report (or download it as .json) and send it however you like. Nothing is transmitted automatically.',
    descriptionLabel: 'Description (for you to fill in)',
    descriptionPlaceholder: 'Describe what happened and what you expected…',
    technicalLabel: 'Technical context (read-only)',
    noPasswords: 'This report does not contain your passwords.',
    copy: 'Copy',
    copied: 'Copied!',
    download: 'Download .json',
    crashTitle: 'The application ran into an error',
    crashHint: 'You can reload the page or report the problem.',
    crashHintInline:
      'This screen ran into an error. You can navigate elsewhere, reload, or report the problem.',
    reload: 'Reload',
  },
  notifications: {
    open: 'Notifications ({count} in progress)',
    title: 'Activity',
    empty: 'No activity',
    loading: 'Loading activity…',
    error: 'Could not load activity',
    clearAll: 'Clear all',
    dismiss: 'Remove',
    cancel: 'Cancel',
    progress: '{done}/{total}',
    cancelForbidden: 'Read-only access: cannot cancel',
    cancelFailed: 'Cancellation failed. Try again.',
    retry: 'Retry',
    retrying: 'Retrying…',
    retryQueued: 'Retried: the job is running again.',
    retryForbidden: 'Read-only access: cannot retry',
    retryAlreadyRunning: 'A job of the same type is already running.',
    retryNotRetryable: 'This job type cannot be retried automatically.',
    retryFailed: 'Retry failed. Try again.',
    eta: 'Estimated to finish in {eta}',
    etaSeconds: '≈ {value} s',
    etaMinutes: '≈ {value} min',
    startedAt: 'Started at {time}',
    ranRange: '{start} → {end}',
    typeScan: 'Scan',
    typeThumbnail: 'Thumbnail generation',
    typeAiTag: 'AI tagging',
    typeDedup: 'Deduplication',
    typeDedupApply: 'Duplicate removal',
    typeDedupExactApply: 'Exact copy removal',
    typeExifPublish: 'EXIF publish',
    typeExifErase: 'EXIF erase',
    typeMove: 'File move',
    typeCompress: 'Lossless compression',
    typeRotate: 'Rotation',
    typeOriginalsCleanup: '_original backups cleanup',
    typeTrashPurge: 'Trash purge',
    typeRenameExtensions: 'Extension fix',
    typeEmptyFoldersCleanup: 'Empty folders cleanup',
    statusQueued: 'Queued',
    statusRunning: 'Running',
    statusDone: 'Done',
    statusFailed: 'Failed',
    statusCanceled: 'Canceled',
    doneNoResult: 'Finished with no result',
    doneNoResultHint: 'Every operation failed. See Admin › File errors.',
    reviewAiTags: 'Review AI tags',
    refreshGallery: 'Refresh gallery',
    // Result summaries of a finished job (bell).
    resultCompressed: '{count} compressed',
    resultBytesSaved: '{bytes} saved',
    resultNoGain: 'No gain',
    resultPotentialGain: 'Simulation · potential gain {bytes} of {total}',
    resultSkipped: '{count} skipped',
    resultFailed: '{count} failure(s)',
    resultMoved: '{count} moved',
    resultRotated: '{count} rotated',
    resultRenamed: '{count} renamed',
    resultConflicts: '{count} conflict(s)',
    resultPurged: '{count} purged',
    resultMissing: '{count} missing',
    resultFoldersRemoved: '{count} folder(s) removed',
    resultSystemFilesRemoved: '{count} system file(s) deleted',
  },
  organize: {
    moveWithAudio: 'Move videos with sound…',
    moveSelection: 'Move selection…',
    moveTitle: 'Move to folder',
    moveWithAudioIntro:
      'Tidy every video with an audio track into a library folder. Files are moved (not copied).',
    moveSelectionIntro:
      'Tidy the {count} selected media into a library folder. Files are moved (not copied).',
    // Destination: folder tree + "New folder here" card (like uploading).
    destLabel: 'Destination folder',
    destSelected: 'Selected destination',
    newFolderHere: 'New folder here',
    searchPlaceholder: 'Search a folder…',
    loadingFolders: 'Loading folders…',
    foldersError: 'Unable to load folders.',
    selectFolder: 'Choose folder {name}',
    folderCount: '{count} file(s)',
    expandFolder: 'Expand {name}',
    collapseFolder: 'Collapse {name}',
    searchEmpty: 'No folder matches this search',
    newFolderConfirm: 'Create a folder in “{parent}”?',
    newFolderCreate: 'Create here',
    newFolderLabel: 'New folder',
    newFolderPreview: 'Will be created: {path}',
    destPlaceholder: 'e.g. with_sound',
    destHint: 'Relative path under the library root (subfolders allowed with /).',
    destInvalid: 'Invalid folder (no absolute path or “..”).',
    submit: 'Move',
    submitting: 'Queuing…',
    cancel: 'Cancel',
    close: 'Close',
    success: 'Move queued. Track progress in the notification bell.',
    errorWriteDisabled: 'Writing is disabled on the library (mounted read-only).',
    error: 'The move failed. Please try again.',
  },
  // XPHOTO-32 (batch 4): writes on FOLDERS themselves — create, rename, move.
  folderOps: {
    newFolder: 'New folder',
    menuLabel: 'Actions for folder {name}',
    actionRename: 'Rename…',
    actionMove: 'Move to…',
    actionCreateChild: 'New subfolder…',
    actionDelete: 'Delete…',
    createTitle: 'New folder',
    renameTitle: 'Rename folder',
    moveTitle: 'Move folder',
    deleteTitle: 'Delete folder',
    createIntro: 'Creates a subfolder in “{folder}”.',
    renameIntro: 'Renames “{folder}”. The folder stays where it is.',
    moveIntro: 'Moves “{folder}” under another folder. Its name is kept.',
    deleteIntro: 'Deletes “{folder}” and all its subfolders. This cannot be undone.',
    nameLabel: 'Folder name',
    namePlaceholder: 'e.g. holidays',
    nameHint: 'A single folder name, without “/” or “\\”.',
    nameInvalid: 'Invalid name (no “/”, no “\\”, no “..” and no name starting with a dot).',
    preview: 'Resulting path: {path}',
    destLabel: 'Destination folder',
    createSubmit: 'Create',
    renameSubmit: 'Rename',
    moveSubmit: 'Move',
    deleteSubmit: 'Delete',
    deleteScanning: 'Inspecting folder…',
    deletePreviewError: 'This folder could not be inspected. Close this window and try again.',
    deleteSafetyNote:
      'Only EMPTY directories are removed: no photo, no video and no file can be erased — with the single exception of the system files listed below, and only if you allow it.',
    deleteBlockedTitle: 'This folder cannot be deleted:',
    blockerIndexedAssets_one:
      '{count} media file is still indexed in this folder. Empty it, or clean up the index in the Admin console (“Trash”, “Missing files”).',
    blockerIndexedAssets_other:
      '{count} media files are still indexed in this folder. Empty it, or clean up the index in the Admin console (“Trash”, “Missing files”).',
    blockerOtherFiles:
      'It still contains files. Move or delete them first — this product never erases a file on your behalf.',
    blockerHiddenDir:
      'It contains a hidden folder (for example Syncthing’s “.stfolder”). Those folders are markers: they are never deleted.',
    blockerUnreadable: 'One of its subfolders could not be read (permissions).',
    deleteFolderCount_one: '{count} directory will be removed.',
    deleteFolderCount_other: '{count} directories will be removed.',
    deleteSystemCount_one: '{count} system file is the only thing preventing deletion.',
    deleteSystemCount_other: '{count} system files are the only thing preventing deletion.',
    deleteSystemNames: 'Files involved: {names}.',
    deleteSystemOptIn: 'Also erase those system files',
    deleteConfirmWord: 'DELETE',
    deleteConfirmLabel: 'To confirm, type “{word}”:',
    impactIntro: 'Will follow automatically:',
    impactAssets_one: '{count} media file',
    impactAssets_other: '{count} media files',
    impactShares_one: '{count} public share',
    impactShares_other: '{count} public shares',
    impactGrants_one: '{count} access grant',
    impactGrants_other: '{count} access grants',
    savedFiltersWarning_one:
      '{count} saved filter references this folder: its expression will NOT be updated and will stop working.',
    savedFiltersWarning_other:
      '{count} saved filters reference this folder: their expressions will NOT be updated and will stop working.',
    errorWriteDisabled: 'Writing is disabled on the library (mounted read-only).',
    errorConflict: 'A folder with that name already exists here. Nothing was changed.',
    errorCrossDevice: 'The destination is on another volume: this move is not supported.',
    errorOutOfSync:
      'The folder was renamed on disk but the database did not follow. Please run a library scan.',
    errorNotEmpty:
      'The folder is no longer empty: its contents changed since the inspection. Nothing was deleted — reopen this window to see the current state.',
    errorHasAssets:
      'Media files are still indexed in this folder. Empty it, or clean up the index in the Admin console (“Trash”, “Missing files”), then try again.',
    errorInvalid: 'Invalid name or destination.',
    errorGeneric: 'The operation failed. Please try again.',
  },
  compress: {
    action: 'Compress selection…',
    title: 'Compress selection',
    intro: 'Shrinks the {count} selected media without any loss of quality.',
    losslessNote:
      'LOSSLESS compression (identical pixels) — JPEG and PNG images only; videos are skipped. A file is replaced only if it becomes smaller.',
    keepOriginal: 'Keep an “_original” backup',
    dryRun: 'Simulate first (preview the savings, without writing)',
    confirm: 'Compress',
    submitting: 'Queuing…',
    cancel: 'Cancel',
    close: 'Close',
    queued: 'Compression queued. Track progress in the notification bell.',
    errorWriteDisabled: 'Writing is disabled on the library (mounted read-only).',
    error: 'Compression failed. Please try again.',
  },
  rotate: {
    // Actions menu entry + "Rotate" dialog (rotation via the EXIF Orientation tag).
    action: 'Rotate selection…',
    title: 'Rotate selection',
    intro: 'Rotates the {count} selected media by writing their orientation.',
    exifNote:
      'Rotation writes ONLY the EXIF orientation tag of the originals — the pixels are never changed (no loss of quality). Videos are skipped.',
    cw: 'Rotate right (90°)',
    ccw: 'Rotate left (90°)',
    flip: 'Turn upside down (180°)',
    dryRun: 'Simulate first (preview, without writing)',
    queued: 'Rotation queued. Track progress in the notification bell.',
    cancel: 'Cancel',
    close: 'Close',
    errorWriteDisabled: 'Writing is disabled on the library (mounted read-only).',
    error: 'Rotation failed. Please try again.',
  },
  fixExtensions: {
    // "Fix extensions" dialog (whole library).
    title: 'Fix extensions',
    intro:
      'Renames files whose extension does not match their real type (a real JPEG named ".png", a WebP named ".jpg", a WEBM video named ".mp4"…) to the correct extension.',
    note: 'The file CONTENT is not changed (a plain rename). On a name collision a "(1)" suffix is added — a file is never overwritten.',
    scanning: 'Scanning…',
    scanError: 'Scan failed.',
    retry: 'Retry',
    none: 'No mislabeled files: every extension matches its content.',
    count: '{count} file(s) to fix:',
    andMore: '… and {count} more.',
    confirm: 'Fix {count} file(s)',
    submitting: 'Queuing…',
    cancel: 'Cancel',
    close: 'Close',
    queued: 'Fix queued. Track progress in the notification bell.',
    errorWriteDisabled: 'Writing is disabled on the library (mounted read-only).',
    error: 'Fix failed. Please try again.',
  },
  cleanEmptyFolders: {
    // "Clean up empty folders" dialog (whole library).
    title: 'Clean up empty folders',
    intro:
      'Removes from disk the folders that no longer hold any file — the ones left behind by a move, a trash purge or a manual cleanup. These folders show up nowhere in xphoto: the folder list is derived from the photos.',
    note: 'No file is ever deleted: the tool only removes empty folders. Hidden folders (starting with a dot) are left untouched — they are often markers used by sync tools.',
    scanning: 'Scanning…',
    scanError: 'Scan failed.',
    retry: 'Retry',
    none: 'No empty folder: nothing to clean up.',
    count: '{count} empty folder(s) to remove:',
    systemCount: '{count} folder(s) holding nothing but system files:',
    systemNote:
      'These folders look empty, but your operating system left a file in them ({names}). They are NOT removed without your consent.',
    systemOptIn: 'Also delete those system files, then remove these folders',
    andMore: '… and {count} more.',
    confirm: 'Remove {count} folder(s)',
    submitting: 'Queuing…',
    cancel: 'Cancel',
    close: 'Close',
    queued: 'Cleanup queued. Track progress in the notification bell.',
    errorWriteDisabled: 'Writing is disabled on the library (mounted read-only).',
    error: 'Cleanup failed. Please try again.',
  },
  upload: {
    title: 'Upload files',
    enlarge: 'Enlarge window',
    reduce: 'Reduce window',
    intro:
      'Add photos or videos to the library. Files are copied into the chosen folder and then indexed (track it in the bell).',
    heicNote:
      'HEIC photos (iPhone) are converted to WebP on import, because browsers cannot display HEIC: the original HEIC is NOT kept. The conversion is irreversible and loses a little quality.',
    dropHint: 'Drag and drop files here, or choose them.',
    chooseFiles: 'Choose files',
    selectedCount: '{count} file(s) selected',
    removeFile: 'Remove {name}',
    destLabel: 'Destination folder',
    destSelected: 'Selected destination',
    newFolderHere: 'New folder here',
    searchPlaceholder: 'Search a folder…',
    loadingFolders: 'Loading folders…',
    foldersError: 'Could not load folders.',
    selectFolder: 'Choose folder {name}',
    folderCount: '{count} file(s)',
    expandFolder: 'Expand {name}',
    collapseFolder: 'Collapse {name}',
    searchEmpty: 'No folder matches this search',
    newFolderConfirm: 'Create a folder in “{parent}”?',
    newFolderCreate: 'Create here',
    newFolderNameLabel: 'New folder name',
    newFolderNamePlaceholder: 'e.g. 2026',
    newFolderNameHint: 'Enter the name of the folder to create in the chosen location.',
    newFolderPreview: 'Will be created: {path}',
    destInvalid: 'Invalid folder (no absolute path or “..”).',
    submit: 'Upload',
    uploading: 'Uploading…',
    cancel: 'Cancel',
    close: 'Close',
    resultUploaded: '{count} file(s) uploaded.',
    resultIndexing: 'Indexing is in progress (track it in the notification bell).',
    resultSkipped: '{count} file(s) skipped:',
    skipNonMedia: 'unsupported type',
    skipBadName: 'invalid file name',
    skipEmpty: 'empty file',
    skipWriteError: 'write failed',
    skipConvertFailed: 'conversion failed',
    skipTooLarge: 'image too large (panorama)',
    errorWriteDisabled:
      'Writing to the library is disabled. An administrator must mount /library writable and enable LIBRARY_WRITABLE.',
    errorForbidden: 'You are not allowed to write to this library.',
    error: 'The upload failed. Please try again.',
  },
  trash: {
    deleteSelection: 'Delete selection',
    // "Move to trash" confirmation — stresses REVERSIBILITY (no file erased from disk;
    // permanent deletion only from administration).
    confirmTitle: 'Move to trash (reversible)',
    confirmBody:
      'Move {count} file(s) to the trash? This is reversible: the files stay restorable from administration and NO file is erased from disk. Permanent deletion remains possible later from administration.',
    confirmDelete: 'Move to trash',
    cancel: 'Cancel',
    deleting: 'Deleting…',
    error: 'Deletion failed. Please try again.',
    errorForbidden: 'You do not have permission to delete these files.',
  },
  bulkRating: {
    rateSelection: 'Rate selection',
    title: 'Rate selection',
    intro: 'Apply a rating to the {count} selected item(s).',
    selected: 'Selected rating: {rating}/5',
    unrated: 'Unrated (no stars)',
    apply: 'Apply',
    applying: 'Saving…',
    cancel: 'Cancel',
    error: 'Rating failed. Please try again.',
    errorForbidden: 'You do not have permission to rate these files.',
  },
  bulkTag: {
    addTag: 'Add a tag',
    tagAiSelection: 'Tag selection with AI',
    title: 'Add a tag',
    intro: 'Apply one or more existing tags to the {count} selected item(s).',
    searchLabel: 'Search a tag',
    searchPlaceholder: 'Filter tags…',
    listLabel: 'Existing tags',
    loading: 'Loading tags…',
    empty: 'No tags — manage the taxonomy to create some.',
    noMatch: 'No tag matches your search.',
    usageCount: 'used {count}×',
    chosenCount: '{count} tag(s) selected',
    apply: 'Apply',
    applying: 'Saving…',
    cancel: 'Cancel',
    error: 'Adding tags failed. Please try again.',
    errorForbidden: 'You do not have permission to tag these files.',
    loadError: 'Could not load tags.',
    // "Remove" mode (tag dialog toggle).
    modeAdd: 'Add',
    modeRemove: 'Remove',
    modeGroupLabel: 'Add or remove tags',
    titleRemove: 'Remove a tag',
    introRemove: 'Remove one or more tags from the {count} selected media.',
    listLabelRemove: 'Tags present on the selection',
    emptyRemove: 'No tag is set on the selection.',
    presentCount: 'on {count} media',
    removeApply: 'Remove',
    removing: 'Removing…',
    removeError: 'Removing tags failed. Please try again.',
    // "Accept AI suggestions" (selection Actions menu).
    acceptAi: 'Accept AI suggestions',
    acceptAiTitle: 'Accept AI suggestions',
    acceptAiBody:
      'The AI-proposed tags on the {count} selected photo(s) are promoted to "validated". The photos stay "To review (AI)" until you finalize them.',
    acceptAiConfirm: 'Accept',
    acceptAiBusy: 'Accepting…',
    acceptAiError: 'Accepting the suggestions failed. Please try again.',
    acceptAiForbidden: 'You do not have permission to validate these tags.',
  },
  aiSwitch: {
    label: 'Enable AI features',
    hintEnabled:
      'AI features are on: “Tag selection with AI” is offered and LLM tagging can be started.',
    hintDisabled:
      'AI features are off: “Tag selection with AI” is hidden for all roles and no AI tagging can be started. A superadmin can re-enable them here.',
    forbidden: 'Access denied: superadmin only.',
    error: 'Could not update the setting. Please try again.',
  },
  llm: {
    tab: 'LLM configuration',
    title: 'LLM configuration',
    intro:
      'Configure the vision provider used to propose tags. Test the connection, discover available models, then save and activate the provider.',
    providerLabel: 'Provider',
    providerLmstudio: 'LM Studio (local)',
    providerOllama: 'Ollama (local)',
    providerClaude: 'Claude (Anthropic)',
    providerGemini: 'Gemini (Google)',
    providerGrok: 'Grok (xAI)',
    activeSuffix: 'Active',
    baseUrlLabel: 'Base URL',
    baseUrlHint:
      'Required for LM Studio (local server). A sensible default is proposed for cloud providers.',
    modelLabel: 'Model',
    modelPlaceholder: 'Model name',
    modelHint: 'Name of the model to use for this provider.',
    modelHintLmstudio: 'Required for this provider (name of the locally loaded model).',
    modelDiscoveredLabel: 'Discovered models',
    modelClickToChoose: 'click to choose',
    modelDiscoveredEmpty: 'This provider exposes no models.',
    loadError: 'Unable to load saved providers. You can still configure one.',
    actionModeLabel: 'AI action mode',
    actionModeAuto: 'Automatic (recommended)',
    actionModeNative: 'Native tools',
    actionModeJson: 'JSON in text',
    actionModeHint:
      'How the AI triggers its actions. "Automatic" tries native tools then falls back to JSON on failure. "Native tools" requires function-calling (capable model). "JSON in text" suits models without function-calling (e.g. Gemma) and avoids a doomed call.',
    captureFloorLabel: 'Capture floor (%)',
    captureFloorHint:
      'The model scores every tag in the taxonomy; only proposals with a score ≥ this floor are kept (the rest are discarded). Default 10%. Lower it to capture more uncertain tags.',
    promptTemplateLabel: 'Indexing prompt (English)',
    promptTemplateHint:
      'Template of the prompt sent to the LLM (in English). Use {{tags}} for the tag list and {{maxTags}} for the max count. Leave empty for the default prompt. The prompt actually sent appears in the AI call log.',
    promptTemplateReset: 'Reset to default prompt',
    apiKeyDetected: 'API key detected',
    apiKeyMissing: 'API key missing: set the environment variable indicated below.',
    apiKeyNotRequired: 'No key required (local execution).',
    apiKeyUnknown: 'Test the connection to check whether the key is present.',
    testConnection: 'Test connection',
    testConnectionOk: 'Connection OK',
    testing: 'Testing…',
    testReachable: 'Provider reachable · {count} model(s) discovered.',
    testUnreachable: 'Provider unreachable.',
    visionNoModel: 'Enter a model to test generation and vision.',
    visionModelOk: 'The model responds (text).',
    visionModelFail: 'The model does not respond.',
    visionImageOk: 'The model accepts images (vision).',
    visionImageFail: 'The model rejects images (not a vision model).',
    saveAndActivate: 'Save and activate',
    saving: 'Saving…',
    saved: 'Provider saved and activated.',
    unsavedChanges: 'Unsaved changes',
    errorModelRequired: 'Enter a model before saving.',
    errorBaseUrlRequired: 'Enter the base URL before saving.',
    errorForbidden: 'Access denied: reserved for the library owner or a superadmin.',
    errorGeneric: 'An error occurred. Please try again.',
    hintsTitle: 'Provider hints',
    hintProvider: 'Provider',
    hintEnvVar: 'Key environment variable',
    hintNoEnvVar: 'None (local execution)',
    hintDoc: 'Console / documentation',
    hintDocLink: 'Open',
    noteLmstudio:
      'Local OpenAI-compatible server: no API key. Start the server, then enter the base URL (default http://localhost:1234/v1) and the model.',
    noteOllama:
      'Local server: no API key. Start Ollama, install a vision model, then enter the base URL (default http://localhost:11434) and the model.',
    noteClaude:
      'Anthropic cloud provider. Create a key in the console, expose it via the XPHOTO_AI_KEY_ANTHROPIC environment variable, then choose a vision model.',
    noteGemini:
      'Google cloud provider. Create a key in AI Studio, expose it via the XPHOTO_AI_KEY_GEMINI environment variable, then choose a vision model.',
    noteGrok:
      'xAI cloud provider (OpenAI-compatible). Create a key in the console, expose it via the XPHOTO_AI_KEY_XAI environment variable, then choose a model.',
  },
  taxonomy: {
    title: 'Taxonomy',
    close: 'Close',
    cancel: 'Cancel',
    intro:
      'Manage your library’s tag vocabulary: one label per tag, no categories or hierarchy. Create, rename, delete, or merge tags.',
    createLabel: 'New tag',
    createPlaceholder: 'Name of the new tag…',
    createAction: 'Create',
    searchLabel: 'Search a tag',
    searchPlaceholder: 'Filter by name…',
    sortLabel: 'Sort by',
    sortName: 'Name',
    sortUsage: 'Usage',
    usageCount: 'used on {count} photos',
    usageNone: 'used on no photos',
    candidate: 'Candidate',
    hideCandidates: 'Hide candidates ({count})',
    loading: 'Loading vocabulary…',
    loadError: 'Could not load the vocabulary.',
    empty: 'No tags yet. Create one above.',
    noMatch: 'No tag matches the search.',
    renameAction: 'Rename',
    renameAria: 'Rename tag {label}',
    renameLabel: 'New tag name',
    renameSave: 'Save',
    renameCancel: 'Cancel',
    descriptionAria: 'Description of tag {label} (optional, steers the AI)',
    descriptionPlaceholder: 'e.g. “trees, flowers, mountains…”',
    descriptionSaving: 'Saving…',
    deleteAction: 'Delete',
    deleteAria: 'Delete tag {label}',
    deleteTitle: 'Delete this tag?',
    deleteBody: 'The tag “{label}” will be permanently removed from the vocabulary.',
    deleteUsedWarning:
      'This tag is used on {count} photos: it will be removed from them. This action cannot be undone.',
    deleteConfirm: 'Delete',
    deleting: 'Deleting…',
    selectForMerge: 'Select tag {label} for merging',
    mergeSelected: '{count} tags selected',
    mergeTargetLabel: 'Keep',
    mergeTargetPlaceholder: 'Choose the tag to keep…',
    mergeAction: 'Merge',
    mergeClear: 'Clear selection',
    mergeHint: 'Select at least two tags to merge them.',
    mergeTitle: 'Merge tags?',
    mergeBody:
      'The {count} tags ({sources}) will be merged into “{target}”: their photos will be reassigned to it, then those tags will be deleted.',
    mergeNote: 'The kept tag then carries all affected photos. This action cannot be undone.',
    mergeConfirm: 'Merge',
    merging: 'Merging…',
    errorDuplicate: 'This tag already exists.',
    errorGeneric: 'The operation failed. Please try again.',
  },
  tagging: {
    title: 'Review / validate tags',
    panelTitle: 'Photo tags',
    listLabel: 'Taxonomy tags',
    validateAndNext: 'Validate and next',
    validateAndFinish: 'Validate and finish',
    validating: 'Validating…',
    confidence: 'AI · {pct}%',
    readonly: 'Read-only access: tags cannot be changed.',
    error: 'Validation failed. Please try again.',
    loading: 'Loading tags…',
    errorLoad: 'Could not load the tags.',
    retry: 'Try again',
    empty: 'No tags in the taxonomy. Create some in “Manage tags”.',
  },
  aiTrace: {
    title: 'AI call log',
    tab: 'AI call log',
    intro:
      'Every AI tagging call to the vision model is logged here: the prompt sent (your tags and their descriptions, with the ban on creating new ones) and the model’s raw response. Useful to verify what is sent and received.',
    total: '{count} logged calls',
    model: 'Model',
    promptLabel: 'Prompt sent',
    responseLabel: 'Model response',
    noResponse: '(no response)',
    reachable: 'Responded',
    unreachable: 'Unreachable',
    summary: '{linked} tags linked · {newLabels} out of taxonomy ignored',
    errorLabel: 'Error',
    prevPage: 'Previous',
    nextPage: 'Next',
    pageStatus: '{from}–{to} of {total}',
    loading: 'Loading the log…',
    errorLoad: 'Could not load the log.',
    retry: 'Try again',
    empty: 'No calls logged',
    emptyHint: 'Select photos, then “Actions…” → “Tag selection with AI” to generate calls.',
  },
  advancedSearch: {
    open: 'Search',
    title: 'Search',
    intro: 'Search by file name, rating and tags. Criteria are combined.',
    filenameSection: 'File name',
    filenamePlaceholder: 'Search by file name…',
    ratingSection: 'Minimum rating',
    ratingAny: 'All ratings',
    ratingAtLeast: 'At least {rating}/5',
    tagsSection: 'Tags',
    tagModeLabel: 'Tag match',
    tagModeAny: 'At least one',
    tagModeAll: 'All',
    apply: 'Apply',
    reset: 'Reset',
    cancel: 'Cancel',
    chipsLabel: 'Active search filters',
    chipFilename: 'Name: {q}',
    chipRating: 'Rating ≥ {rating}★',
    chipTagsAny: 'Tags (at least one): {count}',
    chipTagsAll: 'Tags (all): {count}',
    removeFilename: 'Remove name filter',
    removeRating: 'Remove rating filter',
    removeTags: 'Remove tags filter',
  },
  savedFilters: {
    open: 'My filters',
    none: 'No saved filters',
    loading: 'Loading…',
    loadError: 'Could not load your filters.',
    applyLabel: 'Apply filter {name}',
    saveCurrent: 'Save current filter…',
    editExpr: 'Edit expression…',
    manage: 'Manage my filters…',
    saveTitle: 'Save filter',
    nameLabel: 'Filter name',
    namePlaceholder: 'e.g. Holidays 4★ and up',
    exprPreview: 'Saved expression',
    iconLabel: 'Icon',
    iconNone: 'No icon',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    errorNameTaken: 'That name is already taken.',
    errorStale: 'This filter was changed elsewhere in the meantime. Reload before saving.',
    errorEmptyExpr: 'Build a filter first to save it.',
    errorSave: 'Could not save the filter. Please try again.',
    manageTitle: 'Manage my filters',
    manageEmpty: 'You have no saved filters.',
    renameLabel: 'Rename {name}',
    changeIcon: 'Change icon of {name}',
    delete: 'Delete',
    deleteAria: 'Delete filter {name}',
    moveUp: 'Move {name} up',
    moveDown: 'Move {name} down',
    deleteConfirmTitle: 'Delete filter',
    deleteConfirmBody: 'Delete the filter “{name}”? This action is permanent.',
    deleteConfirm: 'Delete',
    deleting: 'Deleting…',
    errorUpdate: 'Could not update the filter. Please try again.',
    errorReorder: 'Could not reorder the filters. Please try again.',
    errorDelete: 'Could not delete the filter. Please try again.',
    done: 'Close',
    iconName: {
      funnel: 'Funnel',
      star: 'Star',
      camera: 'Camera',
      videoCamera: 'Video camera',
      sparkles: 'Sparkles',
      tag: 'Tag',
      sun: 'Sun',
      heart: 'Heart',
      bookmark: 'Bookmark',
      photo: 'Photo',
    },
  },
  filterExpr: {
    editTitle: 'Advanced expression',
    intro: 'Build a filter with AND, OR, EXCEPT and parentheses.',
    fieldLabel: 'Filter expression',
    placeholder: 'e.g. note>=4 OR (tag:beach AND tag:summer)',
    echoLabel: 'Reads as:',
    empty: 'Type an expression to preview it.',
    paletteLabel: 'Insert a tag',
    connector: 'Chain with',
    connectorOr: 'OR',
    connectorAnd: 'AND',
    connectorExcept: 'EXCEPT',
    paletteSearchLabel: 'Search a tag',
    paletteSearchPlaceholder: 'Search a tag…',
    legendTitle: 'Vocabulary',
    legend: 'type:  note>=  tag:  folder:  name:  status:  ·  AND  OR  EXCEPT  ( )',
    reset: 'Reset',
    saveAs: 'Save as…',
    apply: 'Apply',
    cancel: 'Cancel',
    and: 'and',
    or: 'or',
    except: 'except {x}',
    leaf: {
      tag: 'tagged “{label}”',
      type: 'of type {type}',
      ratingGte: 'rated {value}★ and up',
      ratingLte: 'rated {value}★ and below',
      ratingEq: 'rated exactly {value}★',
      folder: 'in folder “{folder}”',
      folderRoot: 'at the root',
      filename: 'with a name containing “{name}”',
      status: 'with status {status}',
    },
    typeName: {
      image: 'image',
      animatedImage: 'animated image',
      video: 'video',
      videoWithSound: 'video with sound',
    },
    statusName: {
      tagged: 'tagged',
      untagged: 'untagged',
      aiPending: 'to review (AI)',
    },
    error: {
      empty: 'Type an expression.',
      unexpectedToken: 'Unexpected symbol at position {position}.',
      unexpectedEof: 'Incomplete expression: a criterion is expected.',
      unclosedParen: 'Unclosed opening parenthesis.',
      unclosedQuote: 'Unclosed quote.',
      invalidRating: 'Invalid rating (integer from 0 to 5).',
      unknownType: 'Unknown type (image, animée, vidéo, vidéo-son).',
      unknownStatus: 'Unknown status (taguée, non-taguée, à-valider).',
      unknownField: 'Unknown field prefix (type, note, tag, dossier, nom, statut).',
      emptyValue: 'Missing value after the prefix.',
    },
  },
  exprChips: {
    label: 'Active advanced filter',
    advanced: 'Advanced expression',
    remove: 'Remove advanced filter',
  },
  a11y: {
    skipToContent: 'Skip to main content',
    closeMenu: 'Close menu',
  },
};
